/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { FiBox, FiAlertTriangle, FiCheckCircle, FiTag, FiX, FiLayers, FiInfo, FiDollarSign } from "react-icons/fi";
import PageContainer from "../components/PageContainer";
import UnifiedCard from "../components/UnifiedCard";
import ResponsiveTable from "../components/ResponsiveTable";
import { useUI } from "../theme/ThemeProvider";
import { useTenantStorePermissions } from "../../hooks/useTenantStorePermissions";
import NoAccess from "../components/NoAccess";
import { motion, AnimatePresence } from "framer-motion";

export default function AdminStock() {
  const navigate = useNavigate();
  const { theme, lang, colors } = useUI();
  const isDark = theme === "dark";
  const isArabic = lang === "ar";

  const perms = useTenantStorePermissions();

  const baseUrl = (process.env.REACT_APP_API_BASE_URL || "https://madar-api.madar.iq/api").replace(/\/+$/, '');
  const tenantId = parseInt(localStorage.getItem("tenantId") || "0");

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [brandQuery, setBrandQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [quantitySearch, setQuantitySearch] = useState("");
  const [tags, setTags] = useState([]);

  // حالات لتخزين أعداد التقارير الدقيقة
  const [reportZeroCount, setReportZeroCount] = useState(0);
  const [reportLowCount, setReportLowCount] = useState(0);

  const getStatusColor = (status, quantity) => {
    if (quantity === 0 || status === "OUT_OF_STOCK") return colors.red;
    if (status === "LOW_STOCK") return colors.gold;
    if (status === "AVAILABLE" || quantity > 0) return colors.green;
    return colors.text;
  };

  const fetchStock = async () => {
    if (!tenantId || tenantId === -1) return;
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem("authToken");
      const headers = {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "Authorization": token ? `Bearer ${token}` : "",
      };

      // 1. Determine endpoint based on status filter
      let path = "/StockReports/item-stock";
      const url = new URL(`${baseUrl}${path}`);
      url.searchParams.append("tenantId", tenantId.toString());

      if (statusFilter === "OUT_OF_STOCK") {
        url.pathname = url.pathname.replace(path, "/StockReports/zero-stock");
      } else if (statusFilter === "LOW_STOCK") {
        url.pathname = url.pathname.replace(path, "/StockReports/low-stock");
      } else if (statusFilter === "AVAILABLE") {
        url.pathname = url.pathname.replace(path, "/StockReports/available-products");
      } else {
        url.searchParams.append("itemId", "-1");
      }

      const stockRes = await fetch(url.toString(), { headers });
      let stockData = [];
      if (stockRes.ok) {
        const contentType = stockRes.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          stockData = await stockRes.json();
        }
      }

      // 2. Fetch Item Details (Costs/Images)
      const itemsRes = await fetch(`${baseUrl}/GetItems?tenantId=${tenantId}&itemId=-1`, { headers });
      let itemsMap = new Map();
      if (itemsRes.ok) {
        const contentType = itemsRes.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          const itemsData = await itemsRes.json();
          if (Array.isArray(itemsData)) {
            itemsData.forEach(item => itemsMap.set(item.itemId, item));
          }
        }
      }

      // 3. Fetch Tags for Brands
      const tagsRes = await fetch(`${baseUrl}/Tags/get?tenantId=${tenantId}`, { headers });
      let productTagsMap = new Map();
      if (tagsRes.ok) {
        const contentType = tagsRes.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          const tagsData = await tagsRes.json();
          setTags(tagsData);
          tagsData.forEach(tag => {
            if (tag.linkedProductsCount && Array.isArray(tag.linkedProductsCount)) {
              tag.linkedProductsCount.forEach(p => {
                const pid = p.itemId || p.ItemId;
                if (pid) {
                  if (!productTagsMap.has(pid)) productTagsMap.set(pid, []);
                  productTagsMap.get(pid).push(tag.tagName);
                }
              });
            }
          });
        }
      }

      if (Array.isArray(stockData)) {
        // تجميع حسب المنتج (itemId) — سطر واحد لكل منتج بكمية كلية
        const groupedByItem = new Map();

        stockData.forEach((item) => {
          const id = item.itemId || item.ItemId || 0;
          const qty = Number(
            item.TotalStockQty ??
              item.totalStockQty ??
              item.AvailableQty ??
              item.availableQty ??
              item.availableStock ??
              item.AvailableStock ??
              0
          );
          const reorderPoint = Number(item.ReorderPoint ?? item.reorderPoint ?? 0);

          if (!groupedByItem.has(id)) {
            groupedByItem.set(id, {
              item,
              quantity: qty,
              reorderPoint,
              hasLowStock: false,
            });
          } else {
            const g = groupedByItem.get(id);
            g.quantity += qty;
            if (reorderPoint > g.reorderPoint) g.reorderPoint = reorderPoint;
          }

          const g = groupedByItem.get(id);
          if (
            statusFilter === "LOW_STOCK" ||
            (reorderPoint > 0 && qty > 0 && qty <= reorderPoint) ||
            item.stockStatus === "LOW_STOCK" ||
            item.StockStatus === "LOW_STOCK"
          ) {
            g.hasLowStock = true;
          }
        });

        const mapped = Array.from(groupedByItem.entries()).map(([id, g]) => {
          const item = g.item;
          const itemDetails = itemsMap.get(id);
          const qty = g.quantity;

          let currentStatus = "AVAILABLE";
          if (qty === 0) currentStatus = "OUT_OF_STOCK";
          else if (g.hasLowStock || (g.reorderPoint > 0 && qty <= g.reorderPoint))
            currentStatus = "LOW_STOCK";

          const tagsForProduct = productTagsMap.get(id) || [];
          const brand = tagsForProduct.length > 0 ? tagsForProduct.join(" - ") : "—";
          const variant0 = itemDetails?.variants?.[0];
          const cost = Number(variant0?.costPrice || variant0?.cost || item.CostPrice || item.costPrice || 0);

          return {
            id,
            name: item.itemName || item.ItemName || itemDetails?.itemName || "—",
            brand,
            quantity: qty,
            cost,
            totalValue: cost * qty,
            status: currentStatus,
            image: itemDetails?.mainImageUrl || variant0?.imageUrl || null,
          };
        });
        setProducts(mapped);
      }
    } catch (err) {
      console.error("Fetch Error:", err);
      setError(isArabic ? "فشل في تحميل البيانات" : "Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchCounts = async () => {
      if (!tenantId || tenantId === -1) return;
      const token = localStorage.getItem("authToken");
      const headers = { "Authorization": token ? `Bearer ${token}` : "" };

      const endpoints = [
        { key: "zero", url: "/StockReports/zero-stock" },
        { key: "low", url: "/StockReports/low-stock" }
      ];

      for (const ep of endpoints) {
        try {
          const res = await fetch(`${baseUrl}${ep.url}?tenantId=${tenantId}`, { headers });
          if (res.ok) {
            const contentType = res.headers.get("content-type");
            if (contentType && contentType.includes("application/json")) {
              const data = await res.json();
              const count = data[0]?.TotalItemsCount || data[0]?.totalItemsCount || (Array.isArray(data) ? data.length : 0);
              if (ep.key === "zero") setReportZeroCount(count);
              if (ep.key === "low") setReportLowCount(count);
            }
          }
        } catch (e) { console.error(e); }
      }
    };
    fetchCounts();
  }, [baseUrl, tenantId]);

  useEffect(() => {
    fetchStock();
  }, [baseUrl, tenantId, statusFilter]);

  const zeroStock = reportZeroCount;
  const reachedReorderPoint = reportLowCount;
  const availableStockCount = products.filter(p => p.quantity > 0).length;

  const activeBrandsCount = useMemo(() => {
    return tags.filter(tag => tag.isActive === true || tag.isActive === 1).length;
  }, [tags]);

  const filteredProducts = useMemo(() => {
    return products.filter(p => {
      const matchesName = p.name?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesBrand = p.brand?.toLowerCase().includes(brandQuery.toLowerCase());
      let matchesQty = true;
      if (quantitySearch !== "") {
        const searchQty = parseInt(quantitySearch);
        if (!isNaN(searchQty)) matchesQty = p.quantity === searchQty;
      }
      return matchesName && matchesBrand && matchesQty;
    });
  }, [products, searchQuery, brandQuery, quantitySearch]);

  if (!perms.viewStock) return <NoAccess />;

  return (
    <PageContainer title={isArabic ? "المخزون" : "Stock"}>
      {/* التصميم كما هو تماماً - نسخ لصق من ملفك الأصلي */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-10">
        {[
          {
            title: isArabic ? "إجمالي المواد" : "Total Items",
            value: products.length,
            icon: FiLayers,
            color: "#6366f1",
            onClick: () => { setStatusFilter("ALL"); setQuantitySearch(""); setSearchQuery(""); setBrandQuery(""); }
          },
          {
            title: isArabic ? "المنتهية (الكمية صفر)" : "Out of Stock",
            value: zeroStock,
            icon: FiAlertTriangle,
            color: colors.red,
            onClick: () => { setQuantitySearch("0"); setStatusFilter("OUT_OF_STOCK"); }
          },
          {
            title: isArabic ? "المواد الواصلة حد الطلب" : "Reached Reorder Point",
            value: reachedReorderPoint,
            icon: FiBox,
            color: colors.gold,
            onClick: () => { setStatusFilter("LOW_STOCK"); setQuantitySearch(""); }
          },
          {
            title: isArabic ? "المواد المتاحة" : "Available Items",
            value: availableStockCount,
            icon: FiCheckCircle,
            color: colors.green,
            onClick: () => { setStatusFilter("AVAILABLE"); setQuantitySearch(""); }
          },
          {
            title: isArabic ? "عدد البراندات الفعالة" : "Active Brands",
            value: activeBrandsCount,
            icon: FiTag,
            color: colors.mainColor,
            onClick: () => { navigate("/admin-store/categories"); }
          },
        ].map((c, i) => (
          <UnifiedCard key={i} icon={c.icon} title={c.title} value={c.value} valueColor={c.color} statCard={true}>
            <div className="mt-4 pt-3 border-t border-gray-50 dark:border-gray-800 flex justify-between items-center">
              <button onClick={c.onClick} className="text-[11px] font-bold transition-opacity active:opacity-70" style={{ color: colors.mainColor }}>
                {isArabic ? "عرض التفاصيل" : "View Details"}
              </button>
            </div>
          </UnifiedCard>
        ))}
      </div>

      {loading ? (
        <div className="py-12 text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2" style={{ borderColor: colors.mainColor }}></div>
          <p className="mt-4" style={{ color: colors.subText }}>{isArabic ? "جاري تحميل بيانات المخزون..." : "Loading stock data..."}</p>
        </div>
      ) : error ? (
        <div className="py-12 text-center">
          <FiAlertTriangle className="mx-auto text-4xl mb-4" style={{ color: colors.red }} />
          <p style={{ color: colors.subText }}>{isArabic ? `خطأ في تحميل البيانات: ${error}` : `Error loading data: ${error}`}</p>
        </div>
      ) : (
        <>
          <div className="mb-10 animate-in fade-in slide-in-from-top-4 duration-700">
            <div className="rounded-3xl overflow-hidden border shadow-sm" style={{ backgroundColor: colors.cardBg, borderColor: colors.borderColor }}>
              <div className="px-6 py-4 border-b bg-gray-50/50 dark:bg-gray-800/20" style={{ borderColor: colors.borderColor }}>
                <h3 className="font-black text-lg flex items-center gap-2" style={{ color: colors.text }}>
                  <FiBox style={{ color: colors.mainColor }} />
                  {isArabic ? "البحث في المخزون" : "Search in Stock"}
                </h3>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-start">
                  <div className="flex flex-col gap-2">
                    <label className="text-xs font-black uppercase tracking-widest opacity-50" style={{ color: colors.text }}>{isArabic ? "اسم البحث" : "Search Name"}</label>
                    <input type="text" placeholder={isArabic ? "أدخل اسم المنتج..." : "Enter product name..."} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="w-full px-4 py-3 rounded-2xl outline-none transition-all border focus:ring-2" style={{ backgroundColor: isDark ? "#0f172a" : "#ffffff", borderColor: colors.borderColor, color: colors.text, "--tw-ring-color": colors.mainColor + "40" }} />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="text-xs font-black uppercase tracking-widest opacity-50" style={{ color: colors.text }}>{isArabic ? "البراند" : "Brand"}</label>
                    <input type="text" placeholder={isArabic ? "أدخل اسم البراند..." : "Enter brand name..."} value={brandQuery} onChange={(e) => setBrandQuery(e.target.value)} className="w-full px-4 py-3 rounded-2xl outline-none transition-all border focus:ring-2" style={{ backgroundColor: isDark ? "#0f172a" : "#ffffff", borderColor: colors.borderColor, color: colors.text, "--tw-ring-color": colors.mainColor + "40" }} />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="text-xs font-black uppercase tracking-widest opacity-50" style={{ color: colors.text }}>{isArabic ? "الكمية" : "Quantity"}</label>
                    <input type="number" min="0" placeholder={isArabic ? "أدخل الكمية..." : "Enter quantity..."} value={quantitySearch} onChange={(e) => setQuantitySearch(e.target.value)} className="w-full px-4 py-3 rounded-2xl outline-none transition-all border focus:ring-2" style={{ backgroundColor: isDark ? "#0f172a" : "#ffffff", borderColor: colors.borderColor, color: colors.text, "--tw-ring-color": colors.mainColor + "40" }} />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="text-xs font-black uppercase tracking-widest opacity-50" style={{ color: colors.text }}>{isArabic ? "حالة المادة" : "Item Status"}</label>
                    <div className="flex flex-col gap-1">
                      {[
                        { id: "AVAILABLE", label: isArabic ? "متوفر" : "Available", color: colors.green },
                        { id: "LOW_STOCK", label: isArabic ? "حد الطلب" : "Reorder Point", color: colors.gold },
                        { id: "OUT_OF_STOCK", label: isArabic ? "منتهية" : "Finished", color: colors.red },
                        { id: "ALL", label: isArabic ? "الكل" : "All", color: colors.mainColor },
                      ].map((s) => (
                        <button key={s.id} onClick={() => setStatusFilter(s.id)} className="flex items-center justify-between px-4 py-2 rounded-xl text-xs font-black transition-all border-2" style={{ backgroundColor: statusFilter === s.id ? s.color + "15" : "transparent", borderColor: statusFilter === s.id ? s.color : "transparent", color: statusFilter === s.id ? s.color : colors.text, opacity: statusFilter === s.id ? 1 : 0.6 }}>
                          {s.label}
                          {statusFilter === s.id && <FiCheckCircle />}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                {(statusFilter !== "ALL" || quantitySearch !== "" || searchQuery !== "" || brandQuery !== "") && (
                  <div className="mt-6 flex justify-end">
                    <button onClick={() => { setStatusFilter("ALL"); setQuantitySearch(""); setSearchQuery(""); setBrandQuery(""); }} className="flex items-center gap-2 text-xs font-black px-5 py-2.5 rounded-xl transition-all active:scale-105 active:rotate-1 shadow-sm" style={{ backgroundColor: colors.red + "15", color: colors.red }}>
                      <FiX /> {isArabic ? "مسح كافة الفلاتر" : "Clear All Filters"}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="mb-4 flex items-center gap-3">
            <div className="w-2 h-8 rounded-full" style={{ backgroundColor: colors.mainColor }} />
            <h3 className="text-2xl font-black uppercase tracking-tighter" style={{ color: colors.text }}>{isArabic ? "جدول المخزون" : "Stock Table"}</h3>
          </div>

          <ResponsiveTable
            columns={[
              { header: isArabic ? "ت تسلسل" : "SN", align: "center", width: "80px" },
              { header: isArabic ? "اسم منتج" : "Product Name", sortKey: "name", align: "center" },
              { header: isArabic ? "البراند" : "Brand", sortKey: "brand", align: "center" },
              { header: isArabic ? "الكمية" : "Quantity", sortKey: "quantity", align: "center" },
              { header: isArabic ? "التكلفة" : "Cost", sortKey: "cost", align: "center" },
              { header: isArabic ? "المجموع" : "Total", sortKey: "totalValue", align: "center" },
              { header: isArabic ? "عرض المنتج" : "View", align: "center", width: "120px" },
            ]}
            data={filteredProducts}
            searchable={false}
            sortable={true}
            renderRow={(p, index) => (
              <>
                <td className="py-4 px-2 text-center" style={{ color: colors.text, width: "80px" }}>
                  <span className="font-semibold opacity-70">{index + 1}</span>
                </td>
                <td className="py-4 px-2 text-center" style={{ color: colors.text }}>
                  <div className="flex items-center justify-center font-bold text-sm">{p.name}</div>
                </td>
                <td className="py-4 px-2 text-center" style={{ color: colors.subText }}>
                  <span className="px-3 py-1 rounded-full bg-gray-50 dark:bg-gray-800 text-xs border border-gray-100 dark:border-gray-700">{p.brand}</span>
                </td>
                <td className="py-4 px-2 text-center" style={{ color: getStatusColor(p.status, p.quantity) }}>
                  <div className="font-black text-base">{p.quantity}</div>
                </td>
                <td className="py-4 px-2 text-center" style={{ color: colors.text }}>
                  <div className="text-xs font-bold whitespace-nowrap">{p.cost.toLocaleString()} <span className="opacity-50 text-[10px]">{isArabic ? "د.ع" : "IQD"}</span></div>
                </td>
                <td className="py-4 px-2 text-center" style={{ color: colors.mainColor }}>
                  <div className="text-sm font-black whitespace-nowrap">{p.totalValue.toLocaleString()} <span className="opacity-70 text-[10px]">{isArabic ? "د.ع" : "IQD"}</span></div>
                </td>
                <td className="py-4 px-2 text-center" style={{ width: "120px" }}>
                  <button onClick={() => navigate(`/admin-store/products/details/${p.id}`)} className="px-4 py-2 rounded-xl text-xs font-black transition-all active:scale-105 active:rotate-1 shadow-md active:shadow-lg" style={{ backgroundColor: colors.mainColor, color: "#fff" }}>{isArabic ? "عرض المنتج" : "View Product"}</button>
                </td>
              </>
            )}
            renderCard={(p, index) => {
              const statusColor = getStatusColor(p.status, p.quantity);
              return (
                <div className="space-y-3">
                  <div className="flex justify-between items-start">
                    <div className="flex gap-3 items-start">
                      <span className="text-xs font-bold px-2 py-1 rounded bg-gray-100 dark:bg-gray-800" style={{ color: colors.subText }}>#{index + 1}</span>
                      <div>
                        <h3 className="font-bold text-lg" style={{ color: colors.text }}>{p.name}</h3>
                        <p className="text-xs opacity-70" style={{ color: colors.subText }}>{p.brand}</p>
                      </div>
                    </div>
                    <span className="px-3 py-1 rounded-lg text-sm font-semibold" style={{ color: statusColor, backgroundColor: p.status === "OUT_OF_STOCK" ? "rgba(239,68,68,0.1)" : p.status === "LOW_STOCK" ? "rgba(242,180,65,0.1)" : "rgba(34,197,94,0.1)" }}>{p.quantity} {isArabic ? "متاح" : "available"}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm" style={{ color: isDark ? "#94a3b8" : "#6b7280" }}>
                    <div>
                      <p className="text-xs opacity-60 mb-1">{isArabic ? "التكلفة:" : "Cost:"}</p>
                      <p className="font-semibold" style={{ color: colors.text }}>{p.cost.toLocaleString()} {isArabic ? "د.ع" : "IQD"}</p>
                    </div>
                    <div>
                      <p className="text-xs opacity-60 mb-1">{isArabic ? "المجموع:" : "Total:"}</p>
                      <p className="font-bold text-base" style={{ color: colors.mainColor }}>{p.totalValue.toLocaleString()} {isArabic ? "د.ع" : "IQD"}</p>
                    </div>
                  </div>
                  <button onClick={() => navigate(`/admin-store/products/details/${p.id}`)} className="w-full py-2.5 rounded-xl font-bold transition-all mt-2" style={{ backgroundColor: colors.mainColor + '15', color: colors.mainColor, border: `1px solid ${colors.mainColor}30` }}>{isArabic ? "عرض المنتج" : "View Product"}</button>
                </div>
              );
            }}
          />
        </>
      )}
    </PageContainer>
  );
}